/**
 * PatientRow — Compact reusable patient row for the Clinical Day View.
 *
 * Shows: name, last session date, risk badge, severity trend icon.
 * Single-click selects, double-click navigates to patient page.
 */
import React, { useCallback } from "react";
import Tooltip from "@mui/material/Tooltip";
import { Pin, Mic, Share2 } from "lucide-react";
import { renderRiskBadge, getTrendIcon } from "../../utils/PatientsUtils";
import { useFeatureFlags } from "../../contexts/FeatureFlagContext";

const accessLevelLabel = (level) => {
    switch (level) {
        case "view": return "View only";
        case "view_add": return "View + Add";
        case "view_add_private": return "View + Add (Private)";
        case "full": return "Full access";
        default: return "Shared";
    }
};

const PatientRow = ({
    patient,
    onNavigate,
    onRecord,
    showPin = false,
    onPinToggle,
}) => {
    const { flags } = useFeatureFlags();

    const handleClick = useCallback(() => {
        onNavigate?.(patient.id);
    }, [patient.id, onNavigate]);

    const handleRecordClick = useCallback((e) => {
        e.stopPropagation();
        onRecord?.(patient.id);
    }, [patient.id, onRecord]);

    const handlePinClick = useCallback((e) => {
        e.stopPropagation();
        onPinToggle?.(patient);
    }, [patient, onPinToggle]);

    // Format last session date — use documentDate (clinical session date) consistent with PatientsList
    const rawDate = patient.latestDocument?.documentDate || patient.document?.documentDate || null;
    const lastDate = rawDate
        ? new Date(rawDate).toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
        })
        : null;

    const riskBadge = renderRiskBadge(patient, flags);
    const trendIcon = flags.showSeverity && patient.overallSeverity?.trend
        ? getTrendIcon(patient.overallSeverity.trend)
        : null;

    return (
        <div
            className="cdv-patient-row"
            onClick={handleClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === "Enter") handleClick();
                if (e.key === " ") { e.preventDefault(); handleClick(); }
            }}
        >
            <span className="cdv-patient-name userback-ignore">
                {patient.lastName}, {patient.firstName}
                {patient.isShared && patient.shareInfo?.accessLevel && (
                    <Tooltip title={`Shared with you (${accessLevelLabel(patient.shareInfo.accessLevel)})`} arrow>
                        <span
                            className="patients-shared-inline-icon"
                            aria-label="Shared patient"
                            style={{ display: "inline-flex", alignItems: "center", marginLeft: 6, color: "var(--color-info, #2196f3)" }}
                        >
                            <Share2 size={12} />
                        </span>
                    </Tooltip>
                )}
                <Tooltip title="Record session" arrow>
                    <button
                        className="cdv-patient-record-btn"
                        onClick={handleRecordClick}
                        aria-label="Record session for this patient"
                    >
                        <Mic size={12} />
                    </button>
                </Tooltip>
            </span>

            <span className="cdv-patient-meta userback-ignore">
                {riskBadge}
                {trendIcon}
                {lastDate && <span className="cdv-patient-date">{lastDate}</span>}
            </span>

            {showPin && (
                <Tooltip title={patient.pin ? "Unpin" : "Pin to dashboard"} arrow>
                    <button
                        className={`cdv-patient-pin${patient.pin ? " pinned" : ""}`}
                        onClick={handlePinClick}
                        aria-label={patient.pin ? "Unpin patient" : "Pin patient"}
                    >
                        <Pin size={14} />
                    </button>
                </Tooltip>
            )}
        </div>
    );
};

export default PatientRow;
